import { useSyncExternalStore } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export const THEME_KEY = 'odd:theme';
const THEME_EVENT = 'odd:theme-change';

function preference(value: string | null): ThemePreference {
  return value === 'light' || value === 'dark' ? value : 'system';
}

function applyTheme(theme: ThemePreference) {
  // En modo sistema, CSS responde también a los cambios del SO sin recargar.
  if (theme === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}

function onStorage(event: StorageEvent) {
  if (event.key !== THEME_KEY && event.key !== null) return;
  try {
    if (event.storageArea !== localStorage) return;
  } catch {
    return;
  }
  applyTheme(preference(event.newValue));
  window.dispatchEvent(new Event(THEME_EVENT));
}

export function initializeTheme() {
  let theme: ThemePreference = 'system';
  try {
    theme = preference(localStorage.getItem(THEME_KEY));
  } catch {
    /* La preferencia de sistema funciona también con almacenamiento bloqueado. */
  }
  applyTheme(theme);
  // Se mantiene activo aunque el menú de usuario esté cerrado.
  window.addEventListener('storage', onStorage);
}

export function setTheme(theme: ThemePreference) {
  applyTheme(theme);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* El cambio sigue funcionando durante esta sesión. */
  }
  window.dispatchEvent(new Event(THEME_EVENT));
}

function subscribe(onChange: () => void) {
  window.addEventListener(THEME_EVENT, onChange);
  return () => {
    window.removeEventListener(THEME_EVENT, onChange);
  };
}

const getSnapshot = () => preference(document.documentElement.dataset.theme ?? null);
export const useTheme = () => useSyncExternalStore(subscribe, getSnapshot, () => 'system' as const);
